import React, { useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Typography,
  Stack,
  Divider,
} from "@mui/material";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import { cities } from "./cityData.js";
import ReCAPTCHA from "react-google-recaptcha";
import { ApiService } from "@/app/services/AppService";
import { ResponseObject } from "@/app/classes/main.js";
import { useRouter } from "next/navigation.js";

const yearsOfExperience = Array.from({ length: 21 }, (_, i) => i);

const professions = [
  "Beautician",
  "Massage Therapist",
  "Makeup Artist",
  "Hair Stylist",
  "Nail Technician",
];

const genders = ["male", "female", "other"];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    backgroundColor: "rgba(255, 255, 255, 0.92)",
    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
  },
  "& .MuiOutlinedInput-root:hover": {
    backgroundColor: "#fff",
  },
  "& .MuiOutlinedInput-root.Mui-focused": {
    backgroundColor: "#fff",
    boxShadow: "0 0 0 3px rgba(31, 122, 77, 0.12)",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#1f7a4d",
    borderWidth: "1px",
  },
} as const;

interface ProffesionalFormProps {
  selected?: boolean;
}

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

const ProffesionalForm: React.FC<ProffesionalFormProps> = ({ selected }) => {
  const captchaKey = "6LcIOAYrAAAAAAAw1SleyqY-NusL8NJgTO6GJd2W";
  const [captchaValue, setCaptchaValue] = useState(null);
  const [error, setError] = useState("");
  const apiService = new ApiService();
  const router = useRouter();

  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    city: "",
    experience: 0,
    profession: "",
    homeService: true,
    gender: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "mobile" && !/^\d{0,10}$/.test(value)) {
      return;
    }
    setFormData({ ...formData, [name]: value });
    if (name === "mobile") {
      if (value.length === 10) {
        setError("");
      } else {
        setError("Mobile number must be 10 digits.");
      }
    }
  };

  const handleCaptchaChange = (value: any) => {
    setCaptchaValue(value);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!captchaValue) {
      alert("Please verify the reCAPTCHA!");
      return;
    }
    if (formData.mobile.length !== 10) {
      alert("Mobile number must be 10 digits.");
      return;
    }
    const payload = {
      name: formData.name,
      mobileNumber: formData.mobile,
      city: formData.city,
      profession: formData.profession,
      gender: formData.gender,
      experience: formData.experience,
      comfortable: formData.homeService,
      captcha: captchaValue,
    };
    try {
      const responce = await apiService.post<ResponseObject>(
        apiService.expertLeads,
        payload
      );
      if (responce?.status === "success") {
        // Fire Google Ads conversion tracking
        if (
          typeof window !== "undefined" &&
          typeof window.gtag !== "undefined"
        ) {
          window.gtag("event", "conversion", {
            send_to: "AW-10979322157/_KXbCLeNrrgaEK3SrPMo",
            value: 1.0,
            currency: "INR",
          });
        }
        setFormData({
          name: "",
          mobile: "",
          city: "",
          experience: 0,
          profession: "",
          gender: "",
          homeService: true,
        });
        setCaptchaValue(null);
        router.push("/expert-form-submission-thankyou");
      } else {
        console.log("Error:", responce);
      }
    } catch (error) {
      console.log("Error while form submitting: ", error);
    }
  };

  const menuPaperProps = {
    PaperProps: {
      style: { maxHeight: 300, overflow: "auto" as const },
    },
    anchorOrigin: { vertical: "bottom" as const, horizontal: "left" as const },
    transformOrigin: { vertical: "top" as const, horizontal: "left" as const },
  };

  return (
    <Box
      sx={{
        maxWidth: "100%",
        margin: "auto",
        borderRadius: "22px",
        overflow: "hidden",
        boxShadow:
          "0 4px 24px rgba(18, 53, 36, 0.08), 0 24px 56px rgba(18, 53, 36, 0.12)",
        border: "1px solid rgba(18, 53, 36, 0.1)",
        background:
          "linear-gradient(165deg, rgba(255,255,255,0.99) 0%, rgba(246,251,248,0.97) 100%)",
      }}
    >
      <Box
        sx={{
          height: 4,
          width: "100%",
          background:
            "linear-gradient(90deg, #123524 0%, #1f7a4d 45%, #f2c46c 100%)",
        }}
      />
      <Stack spacing={1.25} sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack alignItems="center" spacing={0.75}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(135deg, rgba(223,246,232,0.95) 0%, rgba(255,255,255,0.9) 100%)",
              border: "1px solid rgba(31, 122, 77, 0.2)",
              color: "#1f7a4d",
            }}
          >
            <PersonOutlineRoundedIcon sx={{ fontSize: 24 }} />
          </Box>
          <Typography
            variant="overline"
            component="p"
            sx={{
              textAlign: "center",
              letterSpacing: "0.14em",
              fontWeight: 800,
              fontSize: "0.68rem",
              color: "#1f7a4d",
              m: 0,
            }}
          >
            {selected ? "Apply as a professional" : "पेशेवर के रूप में आवेदन"}
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              textAlign: "center",
              fontWeight: 800,
              color: "#123524",
              fontSize: { xs: "1.15rem", sm: "1.28rem" },
              letterSpacing: "-0.02em",
              lineHeight: 1.25,
            }}
          >
            {selected ? "Submit your details" : "अपना विवरण जमा करें"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              color: "#5e6d63",
              lineHeight: 1.55,
              px: { xs: 0, sm: 1 },
            }}
          >
            {selected
              ? "We'll reach out by call or WhatsApp within 2-3 working days."
              : "हम 2-3 कार्य दिवसों में कॉल या व्हाट्सएप से संपर्क करेंगे।"}
          </Typography>
        </Stack>
        <Divider sx={{ borderColor: "rgba(18, 53, 36, 0.08)" }} />
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label={selected ? "Full name" : "नाम"}
              name="name"
              value={formData.name}
              onChange={handleChange}
              size="small"
              required
              sx={fieldSx}
            />

            <TextField
              fullWidth
              label={selected ? "Mobile number" : "मोबाइल नंबर"}
              name="mobile"
              type="tel"
              value={formData.mobile}
              onChange={handleChange}
              size="small"
              required
              inputProps={{ maxLength: 10, inputMode: "numeric" }}
              placeholder={selected ? "10-digit number" : undefined}
              sx={fieldSx}
              error={!!error}
              helperText={error || undefined}
            />

            <TextField
              fullWidth
              select
              label={selected ? "City" : "शहर"}
              name="city"
              value={formData.city}
              onChange={handleChange}
              size="small"
              required
              SelectProps={{ MenuProps: menuPaperProps }}
              sx={fieldSx}
            >
              {cities?.map((city) => (
                <MenuItem key={city} value={city}>
                  {city}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              select
              label={selected ? "Years of experience" : "वर्षों का अनुभव"}
              name="experience"
              value={formData.experience}
              onChange={handleChange}
              size="small"
              required
              SelectProps={{ MenuProps: menuPaperProps }}
              sx={fieldSx}
            >
              {yearsOfExperience.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              select
              label={selected ? "Profession" : "पेशा"}
              name="profession"
              value={formData.profession}
              onChange={handleChange}
              size="small"
              required
              sx={fieldSx}
            >
              {professions.map((profession) => (
                <MenuItem key={profession} value={profession}>
                  {profession}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              select
              label={selected ? "Gender" : "लिंग"}
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              size="small"
              required
              sx={fieldSx}
            >
              {genders.map((gender) => (
                <MenuItem key={gender} value={gender}>
                  {gender}
                </MenuItem>
              ))}
            </TextField>

            <Box
              sx={{
                borderRadius: "14px",
                border: "1px solid rgba(31, 122, 77, 0.18)",
                background:
                  "linear-gradient(135deg, rgba(223,246,232,0.45) 0%, rgba(255,255,255,0.85) 100%)",
                px: 2,
                py: 1.5,
              }}
            >
              <FormControl component="fieldset" fullWidth sx={{ m: 0 }}>
                <FormLabel
                  sx={{
                    color: "#123524",
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    mb: 0.75,
                    "&.Mui-focused": { color: "#123524" },
                  }}
                >
                  {selected
                    ? "Comfortable visiting clients at home?"
                    : "क्या आप ग्राहकों के घर जाने के लिए तैयार हैं?"}
                </FormLabel>
                <RadioGroup
                  row
                  name="homeService"
                  value={formData.homeService}
                  onChange={handleChange}
                  sx={{
                    gap: 2,
                    flexWrap: "wrap",
                    "& .MuiFormControlLabel-label": { fontSize: "0.9rem" },
                  }}
                >
                  <FormControlLabel
                    value={true}
                    control={
                      <Radio
                        sx={{
                          color: "#5e6d63",
                          "&.Mui-checked": { color: "#1f7a4d" },
                        }}
                      />
                    }
                    label={selected ? "Yes" : "हाँ"}
                  />
                  <FormControlLabel
                    value={false}
                    control={
                      <Radio
                        sx={{
                          color: "#5e6d63",
                          "&.Mui-checked": { color: "#1f7a4d" },
                        }}
                      />
                    }
                    label={selected ? "No" : "नहीं"}
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 0.5,
                px: { xs: 0, sm: 1 },
                borderRadius: "12px",
                bgcolor: "rgba(246, 251, 248, 0.6)",
                border: "1px dashed rgba(18, 53, 36, 0.12)",
              }}
            >
              <ReCAPTCHA sitekey={captchaKey} onChange={handleCaptchaChange} />
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              sx={{
                mt: 0.5,
                py: 1.35,
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "1.02rem",
                background:
                  "linear-gradient(135deg, #123524 0%, #1f7a4d 55%, #1a6b42 100%)",
                boxShadow: "0 10px 28px rgba(18, 53, 36, 0.28)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #0f2d1f 0%, #1a6b42 100%)",
                  boxShadow: "0 12px 32px rgba(18, 53, 36, 0.32)",
                },
              }}
            >
              {selected ? "Submit application" : "आवेदन जमा करें"}
            </Button>
          </Stack>
        </form>
      </Stack>
    </Box>
  );
};

export default ProffesionalForm;
